import { XMarkIcon } from '@heroicons/react/24/outline'
import { format, parseISO } from 'date-fns'
import { SyncLoader } from 'react-spinners'
import { useScheduledWorkHistoryQuery } from '../../api/client'
import { statusChipClasses } from './status-chip'
import { isOneOffFetchTag } from './tags'

type Props = {
    /** The work whose runs to show. `null` closes the modal. */
    tag: string | null
    onClose: () => void
}

/**
 * Why a run exists.
 */
const reasonText = (tag: string, scheduledBy: string) => {
    if (scheduledBy.startsWith('backoff:')) {
        return `Retry after ${scheduledBy.slice('backoff:'.length)}`
    }
    // Redundant while this modal is scoped to one tag, and kept so the rule
    // reads the same here as in the account view, which spans several.
    if (isOneOffFetchTag(tag)) return 'Historic fetch'
    if (scheduledBy.startsWith('cron:')) return 'Scheduled run'
    if (scheduledBy.startsWith('every:')) return 'Scheduled run'
    if (scheduledBy === 'hint') return 'Time chosen by the job itself'
    if (scheduledBy === 'defer') return 'Put off by a rate limit'
    if (scheduledBy === 'skip') return 'Replaced a cancelled run'
    if (scheduledBy === 'user') return 'Asked for by hand'
    return scheduledBy
}

const timestamp = (value?: string | null) => {
    if (!value) return '—'
    try {
        return format(parseISO(value), 'dd/MM/yyyy HH:mm:ss')
    } catch {
        return value
    }
}

const duration = (ms?: number | null) => {
    if (ms == null) return '—'
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(1)}s`
}

/**
 * Every run of one piece of work, newest first.
 */
function ScheduledWorkHistoryModal({ tag, onClose }: Props) {
    const { data, loading, error } = useScheduledWorkHistoryQuery({
        variables: { tag: tag ?? '' },
        skip: !tag,
        fetchPolicy: 'cache-and-network'
    })

    if (!tag) return null

    const runs = data?.scheduledWorkHistory ?? []

    return (
        <div
            className="fixed z-10 inset-0 overflow-y-auto"
            aria-labelledby="work-history-title"
            role="dialog"
            aria-modal="true"
        >
            <div className="flex items-end justify-center pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                <div
                    className="fixed inset-0 bg-ink-500 bg-opacity-75 transition-opacity"
                    aria-hidden="true"
                    onClick={onClose}
                />

                <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                    &#8203;
                </span>

                <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
                    <div className="flex items-start justify-between">
                        <div>
                            <h3 className="text-lg leading-6 text-ink-900" id="work-history-title">
                                History
                            </h3>
                            <p className="mt-1 text-sm text-ink-500 font-mono">{tag}</p>
                        </div>
                        <button onClick={onClose} aria-label="Close history" title="Close">
                            <XMarkIcon className="h-5 w-5 text-ink-400 hover:text-ink-600" />
                        </button>
                    </div>

                    <div className="mt-4 max-h-[60vh] overflow-y-auto">
                        {loading && runs.length === 0 ? (
                            <div className="p-6 flex justify-center">
                                <SyncLoader color="#dc3b32" size="6px" />
                            </div>
                        ) : error ? (
                            <p className="p-6 text-sm text-severe-600">
                                Could not load history: {error.message}
                            </p>
                        ) : runs.length === 0 ? (
                            <p className="p-6 text-sm text-ink-500">
                                No runs recorded. Either this work has never existed, or its rows
                                have been purged.
                            </p>
                        ) : (
                            <table className="min-w-full divide-y divide-ink-200">
                                <thead className="bg-ink-50 sticky top-0">
                                    <tr>
                                        {['Row', 'Status', 'Why', 'Started', 'Took', 'What happened'].map(
                                            (heading) => (
                                                <th
                                                    key={heading}
                                                    className="px-3 py-2 text-left text-xs font-medium text-ink-500 uppercase tracking-wider"
                                                >
                                                    {heading}
                                                </th>
                                            )
                                        )}
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-ink-200">
                                    {runs.map((run) => (
                                        <tr key={run.rowId}>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-500 font-mono">
                                                {run.rowId}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <span
                                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${statusChipClasses(
                                                        run.status
                                                    )}`}
                                                >
                                                    {run.status}
                                                </span>
                                                {run.attempt > 1 ? (
                                                    <span className="ml-1 text-xs text-ink-500">
                                                        try {run.attempt}
                                                    </span>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-2 text-sm text-ink-900">
                                                {reasonText(run.tag, run.scheduledBy)}
                                                {run.deferralCount > 0 ? (
                                                    <div className="text-xs text-ink-500">
                                                        put off {run.deferralCount}{' '}
                                                        {run.deferralCount === 1 ? 'time' : 'times'}
                                                    </div>
                                                ) : null}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-500">
                                                {timestamp(run.startedAt ?? run.scheduledAt)}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-ink-500">
                                                {duration(run.durationMs)}
                                            </td>
                                            {/* The point of the whole modal. Not
                                                truncated: a stack trace or an
                                                Apify error is why you opened
                                                this. */}
                                            <td className="px-3 py-2 text-sm text-ink-700 max-w-md break-words">
                                                {run.log ?? <span className="text-ink-400">—</span>}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>

                    <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <button
                            type="button"
                            onClick={onClose}
                            className="w-full inline-flex justify-center rounded-md border border-ink-300 shadow-sm px-4 py-2 bg-white text-base text-ink-700 hover:bg-ink-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-arwen-500 sm:w-auto sm:text-sm"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ScheduledWorkHistoryModal
